import * as React from "react";
import PropTypes from "prop-types";
import cs from "classnames";

import { LinkGenerator } from "@lululemon/ecom-pattern-library-link-generator";
import Button from "../../quiz-button/quiz-button";
import {
  updatePageId,
  trackBraQuizInteraction,
} from "../../../../lib/analytics";
import quizDataContext from "../../utils/quizDataContext";
import { trackBqPDPInteraction } from "./../../utils";

import styles from "./quiz-unsupported-size.module.scss";

const QuizUnsupportedSize = ({ onResetQuiz, isEmbedded }) => {
  const initialFocusRef = React.useRef();
  const { unsupportedSize } = React.useContext(quizDataContext);
  const { title, subTitle, text, analyticsId, callToActions } = unsupportedSize;
  const { shopAllWomens, viewSizeGuide, retakeBraQuiz } = callToActions;

  updatePageId(analyticsId);

  React.useEffect(() => {
    if (!isEmbedded) {
      trackBraQuizInteraction("no-results");
    }

    initialFocusRef?.current?.focus();
  }, []);

  const trackBqPDPCta = (text) =>
    trackBqPDPInteraction({
      id: "quiz:bra-size",
      type: "Button",
      text,
      attributes: {
        details: "results",
      },
    });

  return (
    <div
      data-testid="quiz-unsupported"
      className={cs(styles.quizUnsupportedSize, "container-fluid", {
        [styles.quizUnsupportedSizeEmbedded]: isEmbedded,
      })}
    >
      <p
        className={cs(
          "lll-font-weight-semibold",
          styles.quizUnsupportedSizeHeading,
          {
            [styles.quizUnsupportedSizeHeadingEmbbeded]: isEmbedded,
            "lll-text-body-3": isEmbedded,
            "lll-text-body-2": !isEmbedded,
          }
        )}
        tabIndex="-1"
        ref={initialFocusRef}
      >
        {title}
      </p>
      <h1
        className={cs("lll-text-small", styles.quizUnsupportedSizeSubHeading, {
          [styles.quizUnsupportedSizeSubHeadingEmbbeded]: isEmbedded,
        })}
      >
        {subTitle}
      </h1>
      <p
        className={cs(
          "lll-text-body-1",
          "lll-font-weight-medium",
          styles.quizUnsupportedSizeText,
          {
            [styles.quizUnsupportedSizeTextEmbbeded]: isEmbedded,
          }
        )}
      >
        {text}
      </p>
      {!isEmbedded && (
        <div className={styles.quizUnsupportedSizeButtonBox}>
          <Button
            isVisible
            role="button"
            href={shopAllWomens.callToActionLink}
            text={shopAllWomens.callToAction}
            onClick={() => trackBraQuizInteraction("shopAllWomens")}
            className={styles.quizUnsupportedSizeButton}
          />

          <div className={styles.quizUnsupportedSizeLinksContainer}>
            <LinkGenerator
              href={viewSizeGuide.callToActionLink}
              role="link"
              className={cs(
                "lll-link-primary",
                "lll-text-body-2",
                "lll-text-center",
                styles.quizUnsupportedSizeSizeGuide
              )}
            >
              {viewSizeGuide.callToAction}
            </LinkGenerator>
            <LinkGenerator
              href="#"
              tabIndex="0"
              role="link"
              className={cs(
                "lll-link-primary",
                "lll-text-body-2",
                "lll-text-center",
                styles.quizUnsupportedSizeRetakeCta
              )}
              onClick={() => {
                trackBraQuizInteraction("retake");
                onResetQuiz();
              }}
            >
              {retakeBraQuiz.callToAction}
            </LinkGenerator>
          </div>
        </div>
      )}

      {isEmbedded && (
        <div
          className={cs(
            styles.quizUnsupportedSizeButtonBox,
            styles.quizUnsupportedSizeButtonBoxEmbedded
          )}
        >
          <Button
            block
            isVisible
            kind="tertiary"
            role="button"
            href="#"
            text={retakeBraQuiz.callToAction}
            onClick={() => {
              trackBqPDPCta("retake-the-quiz");
              onResetQuiz();
            }}
            className={styles.quizUnsupportedSizeButton}
          />
          <Button
            block
            isVisible
            role="button"
            href={viewSizeGuide.callToActionLink}
            text={viewSizeGuide.callToAction}
            onClick={() => trackBqPDPCta("view-size-guide")}
            className={styles.quizUnsupportedSizeButton}
          />
        </div>
      )}
    </div>
  );
};

QuizUnsupportedSize.defaultProps = {
  isEmbedded: false,
};

QuizUnsupportedSize.propTypes = {
  onResetQuiz: PropTypes.func.isRequired,
  isEmbedded: PropTypes.bool,
};

export default QuizUnsupportedSize;
